<template>
  <ModalDialog
    ref="dialog"
    sizeClass="modal-xl"
    :title="title"
    :submitting="submitting"
    :submitTitle="submitTitle"
    icon="fa-barcode-alt"
    @closed="onClosed"
    @submitted="onSubmitted">

    <LoadingPanel :loading="loading" :loadingError="loadingError" />
    <FormGroup v-bind:form="dialogForm" :labelColumnSize="3" v-if="!loading && !loadingError">
      <TabbedBox>
        <template v-slot:tablist>
          <Tab :first="true" tabid="tab-info">
            <i class="far fa-print"></i> Label Configuration
          </Tab>
          <Tab tabid="tab-colour-codes">
            <i class="far fa-palette"></i> Colour Codes
          </Tab>
        </template>
        <template v-slot:tabcontent>
          <TabPage :first="true" tabid="tab-info">
            <ContentTitle>Print Label Configuration</ContentTitle>

            <FormRow label="Name">
              <FormInputText placeholder="Name" v-model="printLabelConfig.name" field="name" />
            </FormRow>
            <FormRow label="Type">
              <FormSelect placeholder="Type" :options="PrintLabelTypeLookup" v-model="printLabelConfig.type" field="type" />
            </FormRow>
            <FormRow label="Active">
              <FormCheckbox v-model="printLabelConfig.isActive" field="isActive" />
            </FormRow>
            <FormRow label="Template Type">
              <FormSelect placeholder="Template Type" :options="templateTypeLookup" v-model="printLabelConfig.templateType" field="templateType" />
            </FormRow>
            <FormRow label="Landscape">
              <FormCheckbox v-model="printLabelConfig.labelLandscape" field="labelLandscape" />
            </FormRow>
            <FormRow label="Size" :fieldCount="2">
              <template v-slot:field1>
                <FormInputText type="number" v-model="printLabelConfig.labelWidth" field="labelWidth" prepend="Width" addon="mm" />
              </template>
              <template v-slot:field2>
                <FormInputText type="number" v-model="printLabelConfig.labelHeight" field="labelHeight" prepend="Height" addon="mm" />
              </template>
            </FormRow>
            <FormRow label="Margin" :fieldCount="4">
              <template v-slot:field1>
                <FormInputText type="number" v-model="printLabelConfig.labelMarginTop" field="labelMarginTop" prepend="Top" addon="mm" />
              </template>
              <template v-slot:field2>
                <FormInputText type="number" v-model="printLabelConfig.labelMarginRight" field="labelMarginRight" prepend="Right" addon="mm" />
              </template>
              <template v-slot:field3>
                <FormInputText type="number" v-model="printLabelConfig.labelMarginBottom" field="labelMarginBottom" prepend="Bottom" addon="mm" />
              </template>
              <template v-slot:field4>
                <FormInputText type="number" v-model="printLabelConfig.labelMarginLeft" field="labelMarginLeft" prepend="Left" addon="mm" />
              </template>
            </FormRow>
            <FormRow label="Show Diagram" v-show="isConfigTypePanel">
              <FormCheckbox v-model="printLabelConfig.optionShowDiagram" field="optionShowDiagram" />
            </FormRow>
          </TabPage>

          <TabPage tabid="tab-colour-codes">
            <div>
              <ContentTitle>Colour Code Blocks</ContentTitle>
              <ContentSubTitle>Colour codes will be blocked out with black if they do no pass any of the filter tests.</ContentSubTitle>
              <ContentSubTitle>Measurements are absolute and applied before orientation &amp; margins</ContentSubTitle>

              <StaticDataTable class="static-table-list">
                <template v-slot:header>
                  <tr>
                    <th width="8%">Name</th>
                    <th width="5%"><Tooltip label="If set, displays the code text over the colour code box" helpStyle>Code</Tooltip></th>
                    <th width="5%">Left Offset</th>
                    <th width="5%">Top Offset</th>
                    <th width="5%">Width</th>
                    <th width="5%">Height</th>
                    <th width="3%" v-show="isConfigTypePanel"><Tooltip label="If set, displays the code if the associated order is an NCR" helpStyle>NCR</Tooltip></th>
                    <th width="3%" v-show="isConfigTypePanel"><Tooltip label="If set, displays the code if the panel is a recut" helpStyle>Recut</Tooltip></th>
                    <th width="3%" v-show="isConfigTypePanel"><Tooltip label="If set, displays the code if the associated order is flagged as urgent" helpStyle>Urgent</Tooltip></th>
                    <th width="15%" v-show="isConfigTypePanel"><Tooltip label="If set, displays the code if the panel contains any of the production stages" helpStyle>Includes Stage</Tooltip></th>
                    <th width="2%">Actions</th>
                  </tr>
                </template>
                <template v-slot:body>
                  <tr v-for="colourCodeConfig in printLabelConfig.printLabelColourCodes" :key="colourCodeConfig.id">
                    <td>
                      <FormInputText v-model="colourCodeConfig.name" placeholder="Name" />
                    </td>
                    <td>
                      <FormInputText v-model="colourCodeConfig.code" placeholder="Code" />
                    </td>
                    <td>
                      <FormInputText type="number" v-model="colourCodeConfig.dimLeft" placeholder="Left" />
                    </td>
                    <td>
                      <FormInputText type="number" v-model="colourCodeConfig.dimTop" placeholder="Top" />
                    </td>
                    <td>
                      <FormInputText type="number" v-model="colourCodeConfig.dimWidth" placeholder="Width" />
                    </td>
                    <td>
                      <FormInputText type="number" v-model="colourCodeConfig.dimHeight" placeholder="Height" />
                    </td>
                    <td v-show="isConfigTypePanel">
                      <FormCheckbox v-model="colourCodeConfig.filterIsNCR" />
                    </td>
                    <td v-show="isConfigTypePanel">
                      <FormCheckbox v-model="colourCodeConfig.filterIsRecut" />
                    </td>
                    <td v-show="isConfigTypePanel">
                      <FormCheckbox v-model="colourCodeConfig.filterIsUrgent" />
                    </td>
                    <td v-show="isConfigTypePanel">
                      <FormMultiSelect v-model="colourCodeConfig.prodStageFilter.valueList" :options="productionStageLookup" />
                    </td>
                    <td>
                      <ActionDropMenu :tableAction="true">
                        <DropdownMenuItem @click="removeColourCode(colourCodeConfig)" icon="fa-trash-alt">Delete</DropdownMenuItem>
                      </ActionDropMenu>
                    </td>
                  </tr>
                </template>
                
              </StaticDataTable>
              <button class="btn btn-white btn-xs" @click.prevent="addColourCode"><i class="far fa-plus"></i> Add Colour Code</button>

            </div>
          </TabPage>
        </template>
      </TabbedBox>
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import { FilterOptionTypes, PrintLabelTypes, PrintLabelTypeLookup, PrintLabelTemplateTypes, PrintLabelTemplateTypeLookup } from '@/store/types'

export default {
  name: 'PrintLabelConfigDialog',
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog
  },
  props: {
    isCreateMode: Boolean
  },
  data() {
    return {
      loading: false,
      loadingError: '',
      printLabelConfigId: null,
      printLabelConfig: {
        type: '',
        isActive: true,
        templateType: '',
        printLabelColourCodes: []
      },
      productionStageLookup: [],
      dialogForm: {},
      submitting: false
    }
  },
  watch: {
    'printLabelConfig.type': function() {
      switch(this.printLabelConfig.type) {
        case PrintLabelTypes.productionPanel:
          this.printLabelConfig.templateType = PrintLabelTemplateTypes.productionPanelSmall
          break
        case PrintLabelTypes.offcut:
          this.printLabelConfig.templateType = PrintLabelTemplateTypes.offcut
          break
        case PrintLabelTypes.glassBlock:
          this.printLabelConfig.templateType = PrintLabelTemplateTypes.glassBlock
          break
      }

    }
  },
  created() {
    this.PrintLabelTypeLookup = PrintLabelTypeLookup
  },
  mounted() {
    // Load the production stages
    this.$apiService.productionService.getProductionStageList()
    .then(response => {
      this.productionStageLookup = response.data
      .filter(productionStage => productionStage.isActive)
      .map(productionStage => {
        return {
          value: productionStage.id,
          name: productionStage.name
        }
      })
    })
    .catch(err => {
      this.displayNetworkError(err, 'Could not load data')
    })

  },
  computed: {
    title() {
      return this.isCreateMode ? 'New Print Label Config' : 'Edit Print Label Config'
    },
    submitTitle() {
      return this.isCreateMode ? 'Create' : 'Update'
    },
    templateTypeLookup() {
      if(this.printLabelConfig.type == PrintLabelTypes.productionPanel)
        return PrintLabelTemplateTypeLookup.filter(templateType => [PrintLabelTemplateTypes.productionPanelSmall, PrintLabelTemplateTypes.productionPanelLarge].includes(templateType.value))
      if(this.printLabelConfig.type == PrintLabelTypes.offcut)
        return PrintLabelTemplateTypeLookup.filter(templateType => templateType.value == PrintLabelTemplateTypes.offcut)
      if(this.printLabelConfig.type == PrintLabelTypes.glassBlock)
        return PrintLabelTemplateTypeLookup.filter(templateType => templateType.value == PrintLabelTemplateTypes.glassBlock)

      return []
    },
    isConfigTypePanel() {
      return this.printLabelConfig.type == PrintLabelTypes.productionPanel
    },
    specialFlagOptions() {
      return [
        { name: 'NCR', value: 'ncr' },
        { name: 'Recut', value: 'recut' },
        { name: 'Urgent', value: 'urgent' }
      ]
    },
  },
  methods: {
    show(printLabelConfig) {
      // Reset form
      this.printLabelConfig = {
        name: '',
        type: PrintLabelTypes.productionPanel,
        isActive: true,
        templateType: PrintLabelTemplateTypes.productionPanelSmall,
        labelLandscape: false,
        labelWidth: 0,
        labelHeight: 0,
        labelMarginTop: 0,
        labelMarginRight: 0,
        labelMarginBottom: 0,
        labelMarginLeft: 0,
        optionShowDiagram: false,
        printLabelColourCodes: []
      }
      this.dialogForm = {}
      this.submitting = false

      if(!this.isCreateMode && printLabelConfig) {
        this.printLabelConfigId = printLabelConfig.id
        this.requestData()
      }

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    requestData() {
      this.loading = true
      this.loadingError = ''

      this.$apiService.adminService.getPrintLabelConfig(this.printLabelConfigId)
      .then(response => {
        this.printLabelConfig = response.data

        // Cleanup codes
        this.printLabelConfig.printLabelColourCodes.forEach(codeConfig => {
          if(!codeConfig.prodStageFilter) {
            codeConfig.prodStageFilter = {
              type: FilterOptionTypes.include,
              valueList: []
            }
          }
        })
      })
      .catch(err => {
        this.loadingError = 'Error loading config'
      })
      .finally(() => {
        this.loading = false
      })
    },
    removeColourCode(colourCode) {
      this.printLabelConfig.printLabelColourCodes = this.printLabelConfig.printLabelColourCodes.filter(item => item.id != colourCode.id)
    },
    addColourCode() {
      this.printLabelConfig.printLabelColourCodes.push({
        id: `temp-id-` + uuidv4(),
        isNew: true,
        name: '',
        code: '',
        dimLeft: 0,
        dimTop: 0,
        dimWidth: 10,
        dimHeight: 10,
        filterIsNCR: false,
        filterIsRecut: false,
        filterIsUrgent: false,
        prodStageFilter: {
          type: FilterOptionTypes.include,
          valueList: []
        }
      })
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      this.submitting = true

      var updateData = _.cloneDeep(this.$data.printLabelConfig)
      updateData.labelWidth = parseInt(updateData.labelWidth)
      updateData.labelHeight = parseInt(updateData.labelHeight)
      updateData.labelMarginWidth = parseFloat(updateData.labelMarginWidth)
      updateData.labelMarginHeight = parseFloat(updateData.labelMarginHeight)

      updateData.printLabelColourCodes.filter(colourCode => {
        // Remove ID from new colour codes
        if(colourCode.isNew)
          colourCode.id = null
      })
      

      if(this.isCreateMode) {
        this.$apiService.adminService.createPrintLabelConfig(updateData)
        .then(() => {
          this.submitting = false
          this.modalClosed(true)
          this.hide()
        })
        .catch(err => {
          this.submitting = false
          this.$data.dialogForm = this.processFormResponse(err)
        })
      } else {
        this.$apiService.adminService.updatePrintLabelConfig(this.printLabelConfigId, updateData)
        .then(() => {
          this.submitting = false
          this.modalClosed(true)
          this.hide()
        })
        .catch(err => {
          this.submitting = false
          this.$data.dialogForm = this.processFormResponse(err)
        })
      }
    }
  }
}
</script>